import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import { Grid, Button, Link, Typography } from '@material-ui/core';

// UTILITY
import setYupValidationSchema from '@/utility/validationSchema/validationSchema';

// HOOKS
import useActions from '@/hooks/useActions';

// STORE
import { UserActions } from '@/store/actions';

// COMPONENTS
import DialogChangeCard from '@/components/Dialogs/DialogChangeCard';
import StripeCardFields from '@/components/Payment/StripeCardFields';
import CardFields from '@/components/Payment/CardFields';

// STYLES
import { SStripePaymentFormWrapper } from './styles';
import { getDefaultUserCard } from '@/utility/user';

function StripePaymentForm(props) {
  const {
    initialValues,
    onSubmit = () => {},
    loading,
    disabled,
    price = 0,
    trialDays = 0,
    productType,
    mode = 'payment',
  } = props;
  const [changeCardState, toggleChangeCardState] = useState(false);

  const [t] = useTranslation();

  const [userAddCard] = useActions([
    (stripe, newCard, zipcode, callback) =>
      UserActions.userAddCard.action({ stripe, card: newCard, zipcode, callback }),
  ]);

  const { partner, cards, changeCardLoading } = useSelector(
    ({ partner: partnerStore, user: userStore }) => ({
      partner: partnerStore.data || {},
      cards: userStore.cards,
      changeCardLoading: userStore.loadings.addCard,
    }),
  );

  const isAddCardMode = mode === 'addCard';
  const isDeviceProductType = productType === 'device_product';
  const hasPartner = !!partner.slug;
  const partnerName = partner.text_overwrites?.partner_name_override || partner.partner_name;
  const disabledSubmitBtn = disabled || loading || changeCardLoading;

  const defaultCard = getDefaultUserCard(cards, true);

  function handleChangeCard(stripeData, cardData, zipcode) {
    userAddCard(stripeData, cardData, zipcode, () => toggleChangeCardState(false));
  }

  const trialInfo = !isAddCardMode && !!price && !!trialDays && (
    <Typography className="p5 m-t-15">
      {t('subscription.payment.trial_info', {
        trial_days: trialDays,
        price,
      })}
    </Typography>
  );

  if (defaultCard) {
    return (
      <SStripePaymentFormWrapper>
        <CardFields card={defaultCard} />
        {trialInfo}
        <Grid className="controls" container spacing={2} alignItems="center">
          {!isAddCardMode && (
            <Grid item xs={12} sm={12} md="auto">
              <Button
                type="submit"
                color="primary"
                disabled={disabledSubmitBtn}
                fullWidth
                onClick={() => onSubmit({ payment_method_id: defaultCard.payment_method_id })}
              >
                {isDeviceProductType ? t('common.purchase') : t('common.subscribe')}
              </Button>
            </Grid>
          )}
          <Grid item xs={12} sm={12} md="auto">
            <Link color="primary" component="button" onClick={() => toggleChangeCardState(true)}>
              {t('buttons.changeCard')}
            </Link>
          </Grid>
        </Grid>
        <DialogChangeCard
          open={changeCardState}
          onClose={() => toggleChangeCardState(false)}
          onSubmit={handleChangeCard}
          loading={changeCardLoading}
        />
      </SStripePaymentFormWrapper>
    );
  }

  if (!defaultCard && isAddCardMode) {
    return (
      <>
        <Typography className="p5 m-b-15">{t('subscription.payment.addNewCard')}</Typography>
        <Link color="primary" component="button" onClick={() => toggleChangeCardState(true)}>
          {t('buttons.addNewCard')}
        </Link>
        <DialogChangeCard
          open={changeCardState}
          onClose={() => toggleChangeCardState(false)}
          onSubmit={handleChangeCard}
          loading={changeCardLoading}
          initialValues={initialValues}
          defaultConfirmState
        />
      </>
    );
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={setYupValidationSchema('stripePaymentSchema', t)}
        validateOnMount
        onSubmit={onSubmit}
      >
        {({ errors, touched, isValid, setFieldValue, setFieldTouched, values }) => {
          return (
            <SStripePaymentFormWrapper>
              <Form>
                <StripeCardFields
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  values={values}
                />
                {trialInfo}
                <Button
                  type="submit"
                  disabled={!isValid || disabledSubmitBtn}
                  color="primary"
                  startIcon={<i className="icon-mb-icon-2" />}
                  fullWidth
                >
                  {isDeviceProductType ? t('common.purchase') : t('common.subscribe')}
                </Button>
              </Form>
            </SStripePaymentFormWrapper>
          );
        }}
      </Formik>
      {hasPartner && (
        <Typography className="p5 m-t-10">
          {t('subscription.payment.partnerDescription', {
            name: partnerName,
          })}
        </Typography>
      )}
    </>
  );
}

StripePaymentForm.defaultProps = {
  initialValues: {},
  loading: false,
  productType: '',
};

StripePaymentForm.propTypes = {
  onSubmit: PropTypes.func,
  initialValues: PropTypes.object,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trialDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  productType: PropTypes.string,
  mode: PropTypes.oneOfType([PropTypes.oneOf(['addCard', 'payment'])]),
};

export default StripePaymentForm;
